import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Mail, MapPin, Phone, Store, Stethoscope } from "lucide-react";
import { Reveal, MaskLine, FadeIn } from "../components/Reveal";
import { CLINIC } from "../data/catalog";
import { WhatsAppGlyph } from "../components/WhatsAppFloat";

const TOPICS = ["Choosing a product", "My order", "Ingredients & suitability", "Stocking Ayushi", "Something else"];

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", topic: TOPICS[0], message: "" });
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const valid = form.name.trim() && form.message.trim();

  const submit = () => {
    if (!valid) return;
    setSent(true);
  };

  return (
    <div className="pt-36 md:pt-44" data-testid="contact-page">
      <header className="mx-auto max-w-7xl px-4 md:px-8">
        <FadeIn>
          <div className="flex items-center gap-4">
            <span className="h-px w-12 bg-ink/30" />
            <p className="eyebrow">Contact</p>
          </div>
        </FadeIn>
        <h1 className="mt-8 font-serif font-light leading-[1.05] text-[11vw] sm:text-6xl lg:text-7xl">
          <MaskLine delay={0.15}>Ask us</MaskLine>
          <MaskLine delay={0.28}>
            <span className="italic text-sage-deep">anything.</span>
          </MaskLine>
        </h1>
        <FadeIn delay={0.5} className="mt-8 max-w-xl">
          <p className="text-base leading-relaxed text-ink/70 md:text-lg">
            A real person answers, Monday to Saturday. Questions about products, ingredients or
            orders — and if it is about skin or hair, Dr. Shibina answers herself.
          </p>
        </FadeIn>
      </header>

      <section className="mx-auto grid max-w-7xl gap-14 px-4 py-16 md:px-8 md:py-20 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <div className="border-t hairline">
            {[
              { icon: WhatsAppGlyph, label: "WhatsApp — the fastest way", value: CLINIC.phone, href: CLINIC.whatsapp, testid: "contact-whatsapp" },
              { icon: Phone, label: "Phone", value: CLINIC.phone, href: `tel:${CLINIC.phoneRaw}`, testid: "contact-phone" },
              { icon: Mail, label: "Email", value: CLINIC.email, href: `mailto:${CLINIC.email}`, testid: "contact-email" },
              { icon: Clock, label: "Hours", value: CLINIC.hours, testid: "contact-hours" },
              { icon: MapPin, label: "Address", value: CLINIC.address, testid: "contact-address" },
            ].map((r, i) => (
              <Reveal key={r.label} delay={i * 0.06}>
                <div className="flex gap-5 border-b hairline py-6" data-testid={r.testid}>
                  <r.icon className="mt-1 h-5 w-5 shrink-0 text-sage-deep" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.24em] text-ink/45">{r.label}</p>
                    {r.href ? (
                      <a href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="mt-2 block text-[15px] text-ink/80 transition-colors hover:text-ink">
                        {r.value}
                      </a>
                    ) : (
                      <p className="mt-2 max-w-md text-[15px] leading-relaxed text-ink/80">{r.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <div className="border hairline bg-white/60 p-6" data-testid="stockist-block">
                <Store className="h-5 w-5 text-sage-deep" strokeWidth={1.5} />
                <h3 className="mt-4 font-serif text-lg italic">Would you like to stock Ayushi?</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-ink/60">
                  For pharmacies, salons and wellness stores. We work with a small number of
                  stockists — get in touch.
                </p>
              </div>
              <Link to="/the-clinic" className="block border hairline bg-white/60 p-6 transition-colors hover:bg-cream-2" data-testid="patient-block">
                <Stethoscope className="h-5 w-5 text-sage-deep" strokeWidth={1.5} />
                <h3 className="mt-4 font-serif text-lg italic">Are you a patient?</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-ink/60">
                  For consultations and clinic appointments, see The Clinic.
                </p>
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal>
            <div className="border hairline bg-white/60 p-8" data-testid="contact-form">
              {sent ? (
                <div data-testid="contact-success">
                  <h2 className="font-serif text-2xl italic">Thank you.</h2>
                  <p className="mt-4 text-sm leading-relaxed text-ink/70">
                    We have your message and we will come back to you within one working day. For
                    anything urgent, WhatsApp us on {CLINIC.phone}.
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="font-serif text-2xl italic">Send a message</h2>
                  <div className="mt-8 space-y-6">
                    <label className="block">
                      <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">Your name</span>
                      <input name="name" value={form.name} onChange={onChange} data-testid="contact-input-name" placeholder="Your name" className="mt-2 w-full border hairline bg-cream px-4 py-3.5 text-[15px] placeholder:text-ink/30" />
                    </label>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">Mobile number</span>
                        <input name="mobile" value={form.mobile} onChange={onChange} data-testid="contact-input-mobile" placeholder="10-digit mobile" className="mt-2 w-full border hairline bg-cream px-4 py-3.5 text-[15px] placeholder:text-ink/30" />
                      </label>
                      <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">Email</span>
                        <input name="email" type="email" value={form.email} onChange={onChange} data-testid="contact-input-email" placeholder="you@example.com" className="mt-2 w-full border hairline bg-cream px-4 py-3.5 text-[15px] placeholder:text-ink/30" />
                      </label>
                    </div>
                    <label className="block">
                      <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">What can we help with?</span>
                      <select name="topic" value={form.topic} onChange={onChange} data-testid="contact-topic" className="mt-2 w-full cursor-pointer border hairline bg-cream px-4 py-3.5 text-[15px]">
                        {TOPICS.map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">Your message</span>
                      <textarea name="message" value={form.message} onChange={onChange} rows={4} data-testid="contact-input-message" placeholder="Write your message" className="mt-2 w-full resize-none border hairline bg-cream px-4 py-3.5 text-[15px] placeholder:text-ink/30" />
                    </label>
                    <button
                      onClick={submit}
                      disabled={!valid}
                      data-testid="contact-send-button"
                      className={`w-full rounded-full px-8 py-4 text-[13px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                        valid ? "bg-ink text-cream hover:bg-ink-deep" : "cursor-not-allowed bg-ink/15 text-ink/40"
                      }`}
                    >
                      Send Message
                    </button>
                  </div>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
